<template>
    <div>
      <h1 class="mb-8 font-bold text-3xl">Customer Orders</h1>

      <modal :show="modals.show_remarks" @closeModal="closeOrderRemarks">
        <div v-html="order_remarks" class="p-8 leading-relaxed"></div>
      </modal>

      <div class="flex flex-col sm:flex-row items-end mb-4">
        <div class="flex w-auto pr-8 my-8 sm:my-0 order-1 sm:order-0 items-center">
          <div class="mr-2">{{ form.items.length }} item{{ form.items.length > 1 ? 's' : ''}} selected</div>

          <button :disabled="form.items.length === 0" @click="modals.assignModal = true" class="btn-primary">Choose Team</button>

          <modal size="md" :show="modals.assignModal" @closeModal="modals.assignModal = false">
            <assign-modal :date="filterForm.date" @assign="assignToTeam"></assign-modal>
          </modal>
        </div>

        <div class="order-0 w-auto sm:order-1 sm:ml-auto grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-none xl:grid-flow-col xl:grid-rows-1 gap-2 items-end">

          <select-input label="Warehouse" v-model="filterForm.warehouse">
            <option :value="null">All</option>
            <option v-for="warehouse in warehouses" :value="warehouse.id">{{ warehouse.name }}</option>
          </select-input>

          <select-input label="Location" v-model="filterForm.location">
            <option :value="null">All</option>
            <option v-for="(label, value) in locations" :value="value">{{ label }}</option>
          </select-input>

          <select-input label="Vessel Location" v-model="filterForm.vessel_location" v-if="filterForm.location === 'island'">
            <option :value="null">All</option>
            <option v-for="location in vesselLocations.island" :value="location">{{ location }}</option>
          </select-input>

          <select-input label="Vessel" v-model="filterForm.boat_id" v-if="filterForm.location === 'island'">
            <option :value="null">All</option>
            <option v-for="vessel in vessels" :value="vessel.id">{{ vessel.name }}</option>
          </select-input>

          <date-input label="Date" v-model="filterForm.date"></date-input>

          <select-input v-model="filterForm.status" label="Status">
            <option value="all">All</option>
            <option value="assigned">Assigned</option>
            <option :value="null">Unassigned</option>
          </select-input>

          <select-input label="Team" v-if="!filterForm.status === 'assigned' || filterForm.status === 'all'" v-model="filterForm.team">
            <option :value="null">All</option>
            <option v-for="team in teams" :value="team.id">{{ team.name }}</option>
          </select-input>

          <div>
            <button class="btn-gray mb-1" @click="reset">Reset</button>
          </div>
        </div>
      </div>

      <div>
        <div class="flex flex-col" v-if="list.data.length">
          <div v-for="order in list.data" class="-my-2 py-2 overflow-x-auto sm:-mx-4 sm:px-4 lg:-mx-4 lg:px-4 mb-1">
            <div class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
              <table class="min-w-full">
                <thead>
                <tr>
                  <th colspan="9" class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    <div class="grid grid-cols-3 xl:grid-flow-col xl:grid-rows-1 gap-2">
                      <div>
                        <inertia-link v-if="canAdmin('access-orders')" :href="route('customer.orders.show',order.id)">Order: <span class="font-bold text-black">{{ order.id }}</span></inertia-link>
                        <span v-else>Order: <span class="font-bold text-black">{{ order.id }}</span></span>
                        <button class="border border-gray-400 rounded p-1" v-if="order.remarks" @click="showOrderRemarks(order)">Show remarks</button>
                      </div>
                      <div>Customer: <span class="font-bold text-black">{{ order.customer_account.name }}</span></div>
                      <div>Location: <span class="font-bold text-black">{{ order.delivery_location }}</span></div>
                      <template v-if="order.delivery_location === 'island'">
                        <div>Vessel: <span class="font-bold text-black">{{ order.vessel }}</span></div>
                        <div>Vessel Location: <span class="font-bold text-black">{{ order.vessel_location }}</span></div>
                      </template>
                      <div v-else>Delivery Address: <span class="font-bold text-black">{{ order.delivery_address }}</span></div>
                      <div>Contact No: <span class="font-bold text-black">{{ order.delivery_contact_number }}</span></div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50">
                    <input type="checkbox" @click.prevent="selectAll(order)" class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" />
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Date
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Warehouse
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Quantity Pending
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Quantity In Progress
                  </th>
                  <th class="px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Team
                  </th>
                </tr>
                </thead>
                <tbody class="bg-white">
                <tr v-for="item in order.items" @click="toggleItem(item)" class="cursor-pointer transition duration-150 ease-in-out" :class="{'bg-yellow-50': isSelected(item.id), 'bg-red-50 cursor-not-allowed': !isSelectable(item)}">
                  <td class="px-3 py-4 whitespace-nowrap text-right border-b border-gray-200 text-sm leading-5 font-medium">
                    <input type="checkbox" :disabled="quantityInProgress(item) > 0" :checked="isSelected(item.id)" class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" />
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap border-b border-gray-200 text-xs leading-4 text-gray-900">
                    {{ item.item_code }}
                  </td>
                  <td class="px-3 py-4 border-b border-gray-200 text-xs leading-4 text-gray-500">
                    {{ item.description }}
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap border-b border-gray-200 text-xs leading-4 text-gray-500">
                    {{ item.handover_date | localDate }}
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap border-b border-gray-200 text-xs leading-4 text-gray-500">
                    {{ item.warehouse.name }}
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap border-b border-gray-200 text-xs leading-4 text-gray-500">
                    <span :class="{'text-red-500': item.base_unit !== item.unit}">{{ item.unit }}</span>
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-4 text-gray-500">
                    <template v-if="item.quantity_pending - quantityInProgress(item) !== item.quantity_approved">
                      <span class="font-bold">{{ item.quantity_pending - quantityInProgress(item) }}</span> / {{ item.quantity_approved }}
                    </template>
                    <span v-else class="font-bold">{{ item.quantity_pending - quantityInProgress(item) }}</span>
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap border-b border-gray-200 text-xs leading-4 text-gray-500">
                    {{ quantityInProgress(item) }}
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap border-b border-gray-200 text-xs leading-4 text-gray-500">
                    {{ item.delivery_team_id ? item.delivery_team.name : 'N/A'}}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <pagination :links="list.links"></pagination>

        </div>
        <div v-else>
          No items found for the selected date
        </div>
      </div>
    </div>
</template>

<script>
  import Layout from '@/Shared/Admin/Layout'
  import Modal from '@/Shared/Modal'
  import {mapValues, pickBy, sumBy, throttle} from 'lodash'
  import DateInput from '@/Shared/DateInput'
  import AssignModal from './AssignModal'
  import SelectInput from '@/Shared/SelectInput'
  import Pagination from '@/Shared/Pagination'
  export default {
    name: 'Index',
    components: {Pagination, SelectInput, AssignModal, DateInput, Modal},
    layout: Layout,

    props: {
      list: Object,
      filters: Object,
      locations: Object,
      warehouses: Array,
      vesselLocations: Object,
      vessels: Array,
      teams: Array,
    },

    watch: {
      filterForm: {
        handler: throttle(function () {
          let query = pickBy(this.filterForm)
          this.form.items = [];
          this.$inertia.get(this.route('master-list', Object.keys(query).length ? query : { remember: 'forget' }), {}, { preserveState: true })
        }, 150),
        deep: true
      }
    },

    data() {
      return {
        modals: {
          assignModal: false,
          show_remarks: false
        },
        form: {
          items: []
        },
        filterForm: {
          date: this.filters.date,
          location: this.filters.location ? this.filters.location : null,
          warehouse: this.filters.warehouse ? this.filters.warehouse : null,
          vessel_location: this.filters.vessel_location ? this.filters.vessel_location : null,
          vessel: this.filters.vessel ? this.filters.vessel : null,
          status: this.filters.status ? this.filters.status : null,
          team: this.filters.team ? this.filters.team : null,
        },
      }
    },

    methods: {

      showOrderRemarks(order) {
        this.order_remarks = order.remarks.replace(/(?:\r\n|\r|\n)/g, '<br />');
        this.modals.show_remarks = true;
      },

      closeOrderRemarks() {
        this.order_remarks = null;
        this.modals.show_remarks = false;
      },

      selectAll(order) {
        let selectedCount = order.items.filter(itm => this.form.items.indexOf(itm.id) !== -1).length
        let mode = selectedCount === order.items.length ? 'unselect' : 'select';

        order.items.forEach(item => {
          let itemState = this.form.items.indexOf(item.id);
          if(mode === 'select' && itemState === -1) {
            this.toggleItem(item)
          } else if (mode === 'unselect' && itemState !== -1) {
            this.toggleItem(item)
          }
        })
      },

      toggleItem(item) {
        if(this.quantityInProgress(item) === 0) {
          let index = this.form.items.indexOf(item.id);
          if (index !== -1) {
            this.form.items.splice(index, 1)
          } else {
            this.form.items.push(item.id);
          }
        }
      },

      quantityInProgress(item) {
        let awaitingHandovers = item.handovers.filter(itm => itm.delivered_at === null)

        if(awaitingHandovers.length) {
          return sumBy(awaitingHandovers, itm => parseFloat(itm.handover_quantity))
        } else {
          return 0;
        }
      },

      assignToTeam(team) {

        this.$inertia.post(this.route('master-list.assign'), {
          items: this.form.items,
          team_id: team.team_id,
          delivery_date: team.delivery_date
        }, {
          onSuccess: () => {
          this.modals.assignModal = false
          this.form.items = []
        }})
      },

      reset() {
        this.filterForm = mapValues(this.filterForm, () => null)
      }
    },

    computed: {
      isSelected() {
        return function (item_id) {
          let index = this.form.items.indexOf(item_id);

          return index !== -1
        }
      },

      isSelectable() {
        return function (item) {
          let inProgress = this.quantityInProgress(item);

          return inProgress === 0;
        }
      }
    }
  }
</script>

<style scoped>

</style>
